// PipelineStepper — Gate G3.7 Phase 0.
//
// Source: ``plan/AI_ALM_Gate_G3_7_Implementation_Plan.md`` §2.2.1.
//
// A sticky-top breadcrumb-stepper rendered immediately under the
// project header. Each pill represents one step in the workflow
// ladder (Phase 1 brief → G1 → Phase 2 → G2 → G2.5 → Phase 3 →
// G3 → G3.5 → Phase 4 → G4 → Phase 5 → G5 → Export).
//
// Pill states:
//
//   * ``done``      — green check, clickable (jumps back for review).
//   * ``current``   — bold accent + dot, no-op on click (already there).
//   * ``attention`` — amber alert, clickable. Gate was emitted but the
//     architect hasn't resolved its decisions yet. Surfaces a real
//     blocker for downstream phases so the user can't mistake it for
//     "locked behind something earlier" — see gate_enforcer.py contract.
//   * ``locked``    — grey hourglass, click shows tooltip ("Locked —
//     finish G3.5 first.").
//   * ``skipped``   — compact "(skipped)" pill, surfaces the architect
//     intentionally elided this step rather than missed it.
//
// The stepper deliberately stays compact (single horizontal row with
// horizontal scroll on narrow viewports) so it doesn't displace the
// FocusSurface. The visual hierarchy is: stepper at top → focus
// surface below → inspector rail to the right.
//
// Plan §P2.13 already shipped a *breadcrumb* eyebrow above each
// surface; the stepper is the global sibling that complements it
// — the breadcrumb tells you what you're looking at, the stepper
// tells you where you are in the larger ladder.

import { AlertCircle, Check, Circle, Hourglass, Lock, SkipForward } from 'lucide-react'

import {
  FOCUS_PILL_LABEL,
  STEPPER_ORDER,
  type FocusKey,
} from './focusKeys'

// --------------------------------------------------------------------------- //
// Types
// --------------------------------------------------------------------------- //
export type StepperState =
  | 'done'
  | 'current'
  | 'attention'
  | 'locked'
  | 'skipped'

export interface StepDescriptor {
  key: FocusKey
  state: StepperState
  /** When state === 'skipped', the architect's recorded reason. */
  skipReason?: string | null
  /** Tooltip text shown on hover (locked + skipped only). */
  tooltip?: string
}

interface PipelineStepperProps {
  /** All steps in canonical workflow order. */
  steps: StepDescriptor[]
  /** The currently-selected focus surface. */
  current: FocusKey | null
  /** Callback fired when the architect clicks a navigable pill. */
  onNavigate: (focus: FocusKey) => void
}

// --------------------------------------------------------------------------- //
// Component
// --------------------------------------------------------------------------- //
export function PipelineStepper({
  steps,
  current,
  onNavigate,
}: PipelineStepperProps) {
  // Filter out steps that aren't in the canonical stepper order
  // (e.g. ``inputs`` / ``artifacts``) — those are sidebar surfaces,
  // not workflow steps.
  const orderedSteps = STEPPER_ORDER.map((key) =>
    steps.find((s) => s.key === key),
  ).filter((s): s is StepDescriptor => Boolean(s))

  if (orderedSteps.length === 0) return null

  return (
    <nav
      aria-label="Pipeline progress"
      className="alm-stepper-shell border-b border-slate-800 bg-slate-950/90 backdrop-blur-sm"
    >
      <ol className="alm-stepper-row flex items-center gap-1.5 overflow-x-auto px-6 py-2">
        {orderedSteps.map((step, idx) => (
          <StepPill
            key={step.key}
            step={step}
            isLast={idx === orderedSteps.length - 1}
            onClick={() => {
              if (
                step.state === 'done' ||
                step.state === 'current' ||
                step.state === 'attention'
              ) {
                onNavigate(step.key)
              } else if (step.state === 'skipped') {
                // Skipped pills navigate back so the architect can
                // unskip them if they change their mind. The Phase
                // 5 backend route accepts a flip from ``skipped``
                // back to ``pending`` via the standard decision API.
                onNavigate(step.key)
              }
              // Locked: no-op, native title attribute provides
              // tooltip guidance.
            }}
          />
        ))}
      </ol>
      {current ? <CurrentMarker steps={orderedSteps} current={current} /> : null}
    </nav>
  )
}

// --------------------------------------------------------------------------- //
// Pill
// --------------------------------------------------------------------------- //
interface StepPillProps {
  step: StepDescriptor
  isLast: boolean
  onClick: () => void
}

function StepPill({ step, isLast, onClick }: StepPillProps) {
  const label = FOCUS_PILL_LABEL[step.key]
  const tooltip =
    step.tooltip ??
    (step.state === 'attention'
      ? `Awaiting your decision — click to review.`
      : step.state === 'locked'
      ? `Locked — finish the previous step first.`
      : step.state === 'skipped'
      ? `Skipped${step.skipReason ? ` — ${step.skipReason}` : ''}`
      : undefined)

  const baseClass =
    'alm-stepper-pill inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-full whitespace-nowrap'

  const stateClass =
    step.state === 'current'
      ? 'bg-indigo-500/20 text-indigo-200 ring-1 ring-indigo-400/60'
      : step.state === 'done'
      ? 'bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 cursor-pointer'
      : step.state === 'attention'
      ? 'bg-amber-500/20 text-amber-200 ring-1 ring-amber-400/60 hover:bg-amber-500/30 cursor-pointer animate-pulse'
      : step.state === 'skipped'
      ? 'bg-slate-700/40 text-slate-400 line-through hover:bg-slate-700/70 cursor-pointer'
      : 'bg-slate-800/60 text-slate-500 cursor-not-allowed'

  const Icon =
    step.state === 'done'
      ? Check
      : step.state === 'current'
      ? Circle
      : step.state === 'attention'
      ? AlertCircle
      : step.state === 'skipped'
      ? SkipForward
      : step.state === 'locked'
      ? Lock
      : Hourglass

  return (
    <li className="alm-stepper-li flex items-center gap-1.5">
      <button
        type="button"
        className={`${baseClass} ${stateClass}`}
        onClick={onClick}
        title={tooltip}
        aria-current={step.state === 'current' ? 'step' : undefined}
        aria-disabled={step.state === 'locked'}
        data-state={step.state}
        data-stepper-key={step.key}
      >
        <Icon
          className={`h-3 w-3 ${
            step.state === 'current' ? 'fill-indigo-300' : ''
          }`}
        />
        <span>{label}</span>
      </button>
      {!isLast && (
        <span aria-hidden className="alm-stepper-sep text-slate-700 text-[10px]">
          ›
        </span>
      )}
    </li>
  )
}

// --------------------------------------------------------------------------- //
// "YOU ARE HERE" caret marker (plan §2.2.1 mockup line)
// --------------------------------------------------------------------------- //
function CurrentMarker({
  steps,
  current,
}: {
  steps: StepDescriptor[]
  current: FocusKey
}) {
  // Compute the relative position of the current pill so the
  // caret floats under it. We can't use a precise pixel offset
  // (the row is overflow-x scrollable), so we rely on the pill's
  // ``data-stepper-key`` and a tiny inline style for the caption.
  const currentStep = steps.find((s) => s.key === current)
  if (!currentStep) return null
  return (
    <div className="alm-stepper-caret px-6 pb-1.5 -mt-1 text-[10px] uppercase tracking-wider text-indigo-300/80">
      You are here · {FOCUS_PILL_LABEL[current]}
    </div>
  )
}
